import { useState } from 'react'
import { cn } from '@/lib/cn'
import { formatCurrency } from '@/lib/format'
import type { AreaGroup, AreaGroupPair, LatePricing } from '@/types/api'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { describeLateWindow } from './describe'
import styles from './AreaPricingPage.module.css'

interface PriceTableProps {
  groups: AreaGroup[]
  pairs: AreaGroupPair[]
  latePricing: LatePricing
}

/** Which ends of the trip the table prices as late. */
type Lateness = 'none' | 'pickup' | 'dropoff' | 'both'

const LATENESS: { value: Lateness; label: string }[] = [
  { value: 'none', label: 'By day' },
  { value: 'pickup', label: 'Pickup late' },
  { value: 'dropoff', label: 'Drop-off late' },
  { value: 'both', label: 'Both late' },
]

/** The server's figure for a pair with these ends late. */
function figure(pair: AreaGroupPair, lateness: Lateness): { minor: number; major: number } {
  switch (lateness) {
    case 'pickup':
      return { minor: pair.pickupLateTotalMinor, major: pair.pickupLateTotal }
    case 'dropoff':
      return { minor: pair.dropoffLateTotalMinor, major: pair.dropoffLateTotal }
    case 'both':
      return { minor: pair.bothLateTotalMinor, major: pair.bothLateTotal }
    default:
      return { minor: pair.totalMinor, major: pair.total }
  }
}

/**
 * What a Cairo booking adds for every pickup group and drop-off group — the
 * rule written out as the table operations will quote from, by day and with
 * either end late at night.
 *
 * Every figure is the server's (`pairs`), computed by the same rules a booking
 * is priced by; this only lays them out. The late figures are there whether or
 * not late-night pricing is on, so the prices can be checked first.
 */
export function PriceTable({ groups, pairs, latePricing }: PriceTableProps) {
  const [lateness, setLateness] = useState<Lateness>('none')

  if (groups.length === 0) return null

  const pairFor = (pickupGroupId: number, dropoffGroupId: number) =>
    pairs.find((pair) => pair.pickupGroupId === pickupGroupId && pair.dropoffGroupId === dropoffGroupId)

  const description =
    lateness === 'none'
      ? 'Rows are where the pickup is, columns where the drop-off is. EGP before VAT, added to every Cairo package.'
      : `Rows are where the pickup is, columns where the drop-off is. EGP before VAT, with the ${
          lateness === 'both' ? 'pickup and the drop-off' : lateness === 'pickup' ? 'pickup' : 'drop-off'
        } ${describeLateWindow(latePricing)}${latePricing.active ? '' : ' — if late-night pricing were on'}.`

  return (
    <Card>
      <CardHeader title="What a trip adds" description={description} />
      <CardBody>
        <div className={styles.segments} role="radiogroup" aria-label="Which ends are late">
          {LATENESS.map((option) => (
            <label key={option.value} className={cn(styles.segment, lateness === option.value && styles.segmentChosen)}>
              <input
                type="radio"
                name="price-table-lateness"
                checked={lateness === option.value}
                onChange={() => setLateness(option.value)}
              />
              {option.label}
            </label>
          ))}
        </div>
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th scope="col">From ↓ to →</th>
                {groups.map((group) => (
                  <th key={group.id} scope="col">
                    {group.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {groups.map((pickup) => (
                <tr key={pickup.id}>
                  <th scope="row">{pickup.name}</th>
                  {groups.map((dropoff) => {
                    const pair = pairFor(pickup.id, dropoff.id)
                    const value = pair ? figure(pair, lateness) : null
                    return (
                      <td key={dropoff.id} className={styles.money}>
                        {value ? (value.minor > 0 ? formatCurrency(value.major) : '0') : '—'}
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
            <caption className={styles.tableCaption}>
              A pickup or drop-off in no sub-area, or in a sub-area with no group, adds nothing for its end, day or
              night.
            </caption>
          </table>
        </div>
      </CardBody>
    </Card>
  )
}
